import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { calculateDuration } from './holidaysService'

/**
 * Get all time off requests for a specific employee
 * @param {string} employeeId - Employee ID
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getRequestsByEmployee = async (employeeId) => {
  try {
    const requestsQuery = query(
      collection(db, 'timeOffRequests'),
      where('employeeId', '==', employeeId),
      orderBy('createdAt', 'desc')
    )

    const querySnapshot = await getDocs(requestsQuery)
    const requests = []

    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: requests }
  } catch (error) {
    console.error('Get requests by employee error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all pending time off requests (admin only)
 * @param {object} userData - Current user data (for filtering by manager)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getPendingRequests = async (userData) => {
  try {
    let requestsQuery

    // Super admin sees ALL pending requests
    if (userData.role === 'super_admin') {
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc')
      )
    } else {
      // Non-super_admin sees only their direct reports' pending requests
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        where('status', '==', 'pending'),
        where('reportsTo', '==', userData.employeeId),
        orderBy('createdAt', 'desc')
      )
    }

    const querySnapshot = await getDocs(requestsQuery)
    const requests = []

    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: requests }
  } catch (error) {
    console.error('Get pending requests error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all time off requests (admin only)
 * @param {object} userData - Current user data (for filtering by manager)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllRequests = async (userData) => {
  try {
    let requestsQuery

    // Super admin sees ALL requests
    if (userData.role === 'super_admin') {
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        orderBy('createdAt', 'desc')
      )
    } else {
      // Non-super_admin sees only their direct reports' requests
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        where('reportsTo', '==', userData.employeeId),
        orderBy('createdAt', 'desc')
      )
    }

    const querySnapshot = await getDocs(requestsQuery)
    const requests = []

    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      })
    })

    return { success: true, data: requests }
  } catch (error) {
    console.error('Get all requests error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Check for overlapping time off requests
 * @param {string} employeeId - Employee ID to check
 * @param {string} startDate - New request start date (YYYY-MM-DD)
 * @param {string} endDate - New request end date (YYYY-MM-DD)
 * @param {string} excludeRequestId - Optional: Request ID to exclude (for edit mode)
 * @returns {Promise<{ success: boolean, hasOverlap: boolean, overlapping: array, error?: string }>}
 */
export const checkOverlap = async (employeeId, startDate, endDate, excludeRequestId = null) => {
  try {
    // Step 1: Fetch ALL requests for this employee with active statuses
    // Query only by employeeId and status (no range queries on dates)
    const requestsQuery = query(
      collection(db, 'timeOffRequests'),
      where('employeeId', '==', employeeId),
      where('status', 'in', ['pending', 'approved'])
    )

    const querySnapshot = await getDocs(requestsQuery)
    const overlapping = []

    // Step 2: Filter in JavaScript - check date overlap
    querySnapshot.forEach((docSnapshot) => {
      const request = { id: docSnapshot.id, ...docSnapshot.data() }

      // Skip if this is the request being edited
      if (excludeRequestId && request.id === excludeRequestId) {
        return
      }

      // Overlap logic: (newStart <= existingEnd) AND (newEnd >= existingStart)
      const newStart = new Date(startDate + 'T00:00:00')
      const newEnd = new Date(endDate + 'T00:00:00')
      const existingStart = new Date(request.startDate + 'T00:00:00')
      const existingEnd = new Date(request.endDate + 'T00:00:00')

      const hasOverlap = (newStart <= existingEnd) && (newEnd >= existingStart)

      if (hasOverlap) {
        overlapping.push({
          id: request.id,
          startDate: request.startDate,
          endDate: request.endDate,
          leaveType: request.leaveType,
          status: request.status,
          duration: request.duration
        })
      }
    })

    return {
      success: true,
      hasOverlap: overlapping.length > 0,
      overlapping: overlapping
    }
  } catch (error) {
    console.error('Check overlap error:', error)
    return { success: false, error: error.message, hasOverlap: false, overlapping: [] }
  }
}

/**
 * Validate request dates
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {{ valid: boolean, error?: string }}
 */
export const validateRequestDates = (startDate, endDate) => {
  // Check if dates are provided
  if (!startDate || !endDate) {
    return { valid: false, error: 'Both start and end dates are required' }
  }

  // Get today's date at midnight
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Parse dates
  const start = new Date(startDate + 'T00:00:00')
  const end = new Date(endDate + 'T00:00:00')

  // Check if start date is in the past
  if (start < today) {
    return { valid: false, error: 'Start date cannot be in the past. Please select today or a future date.' }
  }

  // Check if end date is before start date
  if (end < start) {
    return { valid: false, error: 'End date must be on or after the start date.' }
  }

  return { valid: true }
}

/**
 * Create new time off request
 * @param {object} data - Request data
 * @param {object} userData - User data (employeeId, uid, fullName)
 * @returns {Promise<{ success: boolean, docId?: string, data?: object, error?: string }>}
 */
export const createRequest = async (data, userData) => {
  try {
    // Validate required fields
    if (!data.leaveType || !data.startDate || !data.endDate || !data.reason) {
      return { success: false, error: 'Missing required fields' }
    }

    // Validate dates
    const dateValidation = validateRequestDates(data.startDate, data.endDate)
    if (!dateValidation.valid) {
      return { success: false, error: dateValidation.error }
    }

    // Validate reason length
    if (data.reason.length > 500) {
      return { success: false, error: 'Reason exceeds maximum length of 500 characters' }
    }

    // Calculate duration
    const duration = calculateDuration(data.startDate, data.endDate)

    const now = Timestamp.now()
    const requestData = {
      employeeId: userData.employeeId,
      employeeUid: userData.uid,
      employeeName: userData.fullName || userData.email,
      leaveType: data.leaveType,
      startDate: data.startDate,
      endDate: data.endDate,
      duration: duration,
      reason: data.reason.trim(),
      status: 'pending',
      reportsTo: userData.reportsTo || null,  // CRITICAL FIX 1: Add manager info
      reportsToName: userData.reportsToName || null,  // CRITICAL FIX 1
      reviewedBy: null,
      reviewedByName: null,
      reviewedAt: null,
      reviewComments: null,
      createdAt: now,
      updatedAt: now
    }

    // Create document
    const docRef = await addDoc(collection(db, 'timeOffRequests'), requestData)

    return {
      success: true,
      docId: docRef.id,
      data: {
        id: docRef.id,
        ...requestData
      }
    }
  } catch (error) {
    console.error('Create request error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Approve time off request (admin only)
 * @param {string} requestId - Request document ID
 * @param {object} adminData - Admin user data (uid, fullName)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const approveRequest = async (requestId, adminData) => {
  try {
    // Check if request exists and is pending
    const docRef = doc(db, 'timeOffRequests', requestId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Request not found' }
    }

    const request = docSnap.data()
    if (request.status !== 'pending') {
      return { success: false, error: 'Only pending requests can be approved' }
    }


    // CRITICAL FIX 3: Authorization check with backward compatibility
    // Super_admin can approve anything
    // Non-super_admin can only approve if:
    //   - Request has reportsTo field AND
    //   - reportsTo matches adminData.employeeId
    if (adminData.role !== 'super_admin') {
      if (!request.reportsTo || request.reportsTo !== adminData.employeeId) {
        return {
          success: false,
          error: 'You can only approve requests from your direct reports'
        }
      }
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      status: 'approved',
      reviewedBy: adminData.uid,
      reviewedByName: adminData.fullName || adminData.email,
      reviewedAt: now,
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Approve request error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Reject time off request (admin only)
 * @param {string} requestId - Request document ID
 * @param {object} adminData - Admin user data (uid, fullName)
 * @param {string} comments - Rejection comments (required)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const rejectRequest = async (requestId, adminData, comments) => {
  try {
    // Validate comments
    if (!comments || !comments.trim()) {
      return { success: false, error: 'Rejection comments are required' }
    }

    if (comments.length > 500) {
      return { success: false, error: 'Comments exceed maximum length of 500 characters' }
    }

    // Check if request exists and is pending
    const docRef = doc(db, 'timeOffRequests', requestId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Request not found' }
    }

    const request = docSnap.data()
    if (request.status !== 'pending') {
      return { success: false, error: 'Only pending requests can be rejected' }
    }


    // CRITICAL FIX 3: Authorization check with backward compatibility
    // Super_admin can reject anything
    // Non-super_admin can only reject if:
    //   - Request has reportsTo field AND
    //   - reportsTo matches adminData.employeeId
    if (adminData.role !== 'super_admin') {
      if (!request.reportsTo || request.reportsTo !== adminData.employeeId) {
        return {
          success: false,
          error: 'You can only reject requests from your direct reports'
        }
      }
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      status: 'rejected',
      reviewedBy: adminData.uid,
      reviewedByName: adminData.fullName || adminData.email,
      reviewedAt: now,
      reviewComments: comments.trim(),
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Reject request error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Cancel time off request (employee only, pending requests only)
 * @param {string} requestId - Request document ID
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const cancelRequest = async (requestId) => {
  try {
    // Check if request exists and is pending
    const docRef = doc(db, 'timeOffRequests', requestId)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return { success: false, error: 'Request not found' }
    }

    const request = docSnap.data()
    if (request.status !== 'pending') {
      return { success: false, error: 'Only pending requests can be cancelled' }
    }

    const now = Timestamp.now()
    await updateDoc(docRef, {
      status: 'cancelled',
      updatedAt: now
    })

    return { success: true }
  } catch (error) {
    console.error('Cancel request error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all employees (for admin filter dropdown)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllEmployees = async () => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('fullName', 'asc')
    )

    const querySnapshot = await getDocs(usersQuery)
    const employees = []

    querySnapshot.forEach((doc) => {
      const userData = doc.data()
      employees.push({
        uid: doc.id,
        employeeId: userData.employeeId,
        fullName: userData.fullName,
        email: userData.email
      })
    })

    return { success: true, data: employees }
  } catch (error) {
    console.error('Get all employees error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Helper: Get leave type options
 * @returns {array} - Array of leave types
 */
export const getLeaveTypes = () => {
  return ['Annual Leave', 'Sick Leave', 'Casual Leave']
}

/**
 * Helper: Get status badge variant
 * @param {string} status - Request status
 * @returns {string} - Badge variant
 */
export const getStatusBadgeVariant = (status) => {
  const variants = {
    'pending': 'warning',
    'approved': 'success',
    'rejected': 'error',
    'cancelled': 'info'
  }
  return variants[status] || 'info'
}

/**
 * Helper: Format request date range for display
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {string} - Formatted date range
 */
export const formatRequestDate = (startDate, endDate) => {
  try {
    const start = new Date(startDate + 'T00:00:00')
    const end = new Date(endDate + 'T00:00:00')

    // Single day
    if (startDate === endDate) {
      return start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }

    // Same month and year
    const startYear = start.getFullYear()
    const endYear = end.getFullYear()
    const startMonth = start.getMonth()
    const endMonth = end.getMonth()

    if (startYear === endYear && startMonth === endMonth) {
      const month = start.toLocaleDateString('en-US', { month: 'short' })
      return `${month} ${start.getDate()}-${end.getDate()}, ${startYear}`
    }

    // Different months or years
    const startFormatted = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    const endFormatted = end.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
    return `${startFormatted} - ${endFormatted}`
  } catch (error) {
    console.error('Format request date error:', error)
    return `${startDate} - ${endDate}`
  }
}

/**
 * Helper: Format timestamp for display
 * @param {Timestamp} timestamp - Firebase Timestamp
 * @returns {string} - Formatted date
 */
export const formatTimestamp = (timestamp) => {
  try {
    if (!timestamp) return 'N/A'
    const date = timestamp.toDate()
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch (error) {
    console.error('Format timestamp error:', error)
    return 'Invalid date'
  }
}

/**
 * Helper: Get today's date in YYYY-MM-DD format (for date input min attribute)
 * @returns {string} - Today's date
 */
export const getTodayDate = () => {
  const today = new Date()
  return today.toISOString().split('T')[0]
}

/**
 * Get total approved leave days for an employee
 * @param {string} employeeId - Employee ID
 * @returns {Promise<number>} - Total approved days
 */
export const getApprovedLeaveDays = async (employeeId) => {
  try {
    const requestsQuery = query(
      collection(db, 'timeOffRequests'),
      where('employeeId', '==', employeeId),
      where('status', '==', 'approved')
    )

    const querySnapshot = await getDocs(requestsQuery)
    let totalDays = 0

    querySnapshot.forEach((doc) => {
      const data = doc.data()
      // Use the duration field which already has the calculated days
      totalDays += data.duration || 0
    })

    return totalDays
  } catch (error) {
    console.error('Get approved leave days error:', error)
    return 0
  }
}

/**
 * Get custom leave balance for an employee (if set by admin)
 * @param {string} employeeId - Employee ID
 * @returns {Promise<number|null>} - Custom balance or null if not set
 */
export const getCustomLeaveBalance = async (employeeId, userUid = null) => {
  const candidateDocIds = []
  if (userUid) {
    candidateDocIds.push(userUid)
  }
  if (employeeId && employeeId !== userUid) {
    candidateDocIds.push(employeeId)
  }

  for (const docId of candidateDocIds) {
    try {
      const userDoc = await getDoc(doc(db, 'users', docId))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        return userData.customLeaveBalance !== undefined ? userData.customLeaveBalance : null
      }
    } catch (error) {
      if (error.code === 'permission-denied') {
        // Skip silently for fallback doc IDs (e.g., legacy employeeId documents)
        if (docId === userUid || !userUid) {
          console.error('Custom leave balance access denied for user profile', error)
          return null
        }
        continue
      }
      console.error('Get custom leave balance error:', error)
      return null
    }
  }

  return null
}

/**
 * Update custom leave balance for an employee (super_admin only)
 * @param {string} employeeId - Employee ID
 * @param {number} balance - New leave balance
 * @param {string|null} userUid - Firestore user document ID (uid)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const updateCustomLeaveBalance = async (employeeId, balance, userUid = null) => {
  try {
    // Validate balance
    if (typeof balance !== 'number' || balance < 0 || balance > 365) {
      return { success: false, error: 'Invalid balance value (must be 0-365)' }
    }

    const docId = userUid || employeeId
    if (!docId) {
      return { success: false, error: 'Missing user identifier' }
    }

    const userRef = doc(db, 'users', docId)
    await updateDoc(userRef, {
      customLeaveBalance: balance
    })

    return { success: true }
  } catch (error) {
    console.error('Update custom leave balance error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get all employees for leave balance editing (super_admin only)
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getAllEmployeesForLeaveEdit = async () => {
  try {
    const usersQuery = query(
      collection(db, 'users'),
      orderBy('fullName', 'asc')
    )

    const querySnapshot = await getDocs(usersQuery)
    const employees = []

    querySnapshot.forEach((doc) => {
      const userData = doc.data()
      employees.push({
        uid: doc.id,
        employeeId: userData.employeeId,
        fullName: userData.fullName,
        email: userData.email
      })
    })

    return { success: true, data: employees }
  } catch (error) {
    console.error('Get all employees for leave edit error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get pending requests count based on user role
 * @param {object} userData - Current user data
 * @returns {Promise<number>} - Count of pending requests
 */
export const getPendingRequestsCount = async (userData) => {
  try {
    let requestsQuery

    if (userData.role === 'super_admin') {
      // Count ALL pending requests
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        where('status', '==', 'pending')
      )
    } else if (userData.role === 'admin') {
      // Count pending from direct reports
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        where('status', '==', 'pending'),
        where('reportsTo', '==', userData.employeeId)
      )
    } else {
      // Count own pending requests
      requestsQuery = query(
        collection(db, 'timeOffRequests'),
        where('status', '==', 'pending'),
        where('employeeId', '==', userData.employeeId)
      )
    }

    const querySnapshot = await getDocs(requestsQuery)
    return querySnapshot.size
  } catch (error) {
    console.error('Get pending requests count error:', error)
    return 0
  }
}
